// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Printing SN labels (component design §12): the SN's code and the SN under it, one label
 * per SN — a robot's as a QR code, a module's as a Code 128 barcode. Printed from a filed
 * archive or a filed batch: an SN exists only once its archive does.
 *
 * The codes are drawn as SVG, the way the SN card draws them — sharp on a label printer at
 * any size, nothing fetched. The labels go into a window of their own, which prints them
 * and nothing else of the page; the browser's print dialog picks the printer and the paper.
 */

import { code128Path } from "./code128";
import { qrPath } from "./qr";

/** `qr` for a robot SN, `code128` for a module SN. */
export type Symbology = "qr" | "code128";

export interface Label {
  /** What the code encodes and the big line under it. */
  sn: string;
  symbology: Symbology;
  /** Smaller lines under the SN: the archive number, the module type. */
  lines?: string[];
}

const LABEL_STYLE = `
  @page { margin: 6mm; }
  body { margin: 0; font-family: system-ui, sans-serif; }
  .labels { display: flex; flex-wrap: wrap; gap: 4mm; }
  .label { width: 50mm; padding: 2mm; border: 1px dashed #bbb; text-align: center;
           break-inside: avoid; }
  .label svg.qr { width: 34mm; height: 34mm; }
  .label svg.code128 { width: 46mm; height: 12mm; }
  .sn { font: 600 11pt ui-monospace, monospace; letter-spacing: 0.5px; }
  .line { font-size: 8pt; color: #333; }
  @media print { .label { border-color: transparent; } }
`;

function escape(text: string): string {
  return text.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] ?? char,
  );
}

function symbolSvg(label: Label): string {
  if (label.symbology === "qr") {
    const { path, size } = qrPath(label.sn);
    return (
      `<svg class="qr" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
      `<rect width="${size}" height="${size}" fill="#fff"/><path d="${path}" fill="#000"/></svg>`
    );
  }
  const { path, width } = code128Path(label.sn);
  return (
    `<svg class="code128" viewBox="0 0 ${width} 1" preserveAspectRatio="none" ` +
    `shape-rendering="crispEdges"><rect width="${width}" height="1" fill="#fff"/>` +
    `<path d="${path}" fill="#000"/></svg>`
  );
}

/** The label sheet as a complete HTML document. */
export function labelSheet(labels: Label[], title: string): string {
  const body = labels
    .map((label) => {
      const lines = (label.lines ?? [])
        .map((line) => `<div class="line">${escape(line)}</div>`)
        .join("");
      return (
        `<div class="label">${symbolSvg(label)}` +
        `<div class="sn">${escape(label.sn)}</div>${lines}</div>`
      );
    })
    .join("");
  return (
    `<!doctype html><html><head><meta charset="utf-8"><title>${escape(title)}</title>` +
    `<style>${LABEL_STYLE}</style></head><body><div class="labels">${body}</div></body></html>`
  );
}

/** Open the labels in a window of their own and print them. False when the browser
 *  blocked the window. */
export function printLabels(labels: Label[], title: string): boolean {
  const target = window.open("", "_blank", "width=720,height=900");
  if (!target) return false;
  target.document.open();
  target.document.write(labelSheet(labels, title));
  target.document.close();
  target.focus();
  target.print();
  return true;
}
